import React from 'react';
import Joi from 'joi';
import Cookies from 'cookies';

import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import {
	checkEntityStatus,
	getFullEntityUrl
} from '@culture/common-helpers/entities';
import { getMovieMicrodata } from '@culture/common-helpers/microdata/movie';
import { getEntityRubric } from 'utils/helpers/rubrics';
import { getBreadcrumbs } from 'logic/page';

import Template from 'pageTemplates/entities/movie';
import { fieldsHash } from 'utils/helpers/catalogs';
import { collectVisitedEntitiesInCookies } from 'utils/helpers/recommendations';

const MovieTemplate = (props) => <Template {...props} />;

const getBreadcrumbsParams = (cleanCache, movie) =>
	addCleanCache(
		{
			entityType: 'movies',
			_id: movie._id,
			cached: true
		},
		cleanCache
	);

const getMovie = (params) => {
	const movieParams = addCleanCache(
		{
			withEntityMeta: true,
			expand: [
				'mainRubric',
				'rubrics',
				'materials',
				'institute',
				'participants.person',
				'participants.type',
				'type'
			]
		},
		params.cleanCache
	);

	return apiRequest(`movies/${params._id}`, movieParams);
};

const getPublications = (id, cleanCache) => {
	const params = addCleanCache(
		{
			mentionedMovie: id,
			fields: fieldsHash.publications.join(),
			limit: 100,
			statuses: 'published',
			cached: true
		},
		cleanCache
	);

	return apiRequest('publications', params);
};

const getRecommendations = (movie, cleanCache) => {
	const params = addCleanCache(
		{
			types: movie.type,
			statuses: 'published',
			fields: [
				'title',
				'thumbnailFile',
				'duration',
				'genres',
				'year',
				'name',
				'type',
				'institute',
				'participants',
				'tifloComments',
				'rubrics'
			].join(),
			expand: ['genres', 'participants.type', 'institute'],
			excludeIds: [movie._id],
			limit: 15,
			cached: true
		},
		cleanCache
	);

	return apiRequest('movies', params);
};

export async function getServerSideProps({ query, req, res }) {
	const validationSchema = Joi.object().keys({
		_id: Joi.number().integer().required(),
		name: Joi.array().items(Joi.string()).length(1),
		cleanCache: Joi.boolean()
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const movie = await getMovie(params);

	if (!checkEntityStatus(movie)) {
		return {
			notFound: true
		};
	}

	if (!params.name || movie.name !== params.name[0]) {
		return {
			redirect: {
				destination: getFullEntityUrl(req.url, 'movies', movie),
				permanent: true
			}
		};
	}

	if (movie?._id) {
		const cookies = new Cookies(req, res);
		collectVisitedEntitiesInCookies(
			cookies,
			['movies', 'broadcasts'],
			movie._id
		);
	}

	const rubric = getEntityRubric(movie);

	const [
		breadcrumbs,
		{ items: publications },
		{ items: recommendations }
	] = await batchApiRequest([
		getBreadcrumbs(getBreadcrumbsParams(params.cleanCache, movie)),
		getPublications(movie._id, params.cleanCache),
		getRecommendations(movie, params.cleanCache)
	]);

	const materials = movie.materials?.filter(
		(material) => material.type === 'photo'
	);

	return {
		props: {
			movie,
			microdata: getMovieMicrodata(movie, 'movies'),
			breadcrumbs,
			publications,
			recommendations,
			rubric,
			materials
		}
	};
}

export default MovieTemplate;
